import { ZIP_CODE_CA_REGEX } from '@/constants/regex'
import { Building } from '@/reducers/types'
import { normalizeLink } from '@/utils/link'

import { getPhoneDigits } from '../../../../shared/person-table.utils'
import { PlaceFlags } from '../../../features/places-features.constants'
import { buildFlagsPayload } from '../../../features/places-features.rules'
import { IS_CONVENIENCE_FEE_ENABLED } from '../../../places.gates'
import { toBuildingPhone } from '../../../places.utils'
import {
  getPlaceGeneralDefaults,
  PLACE_GENERAL_FLAG_KEYS,
  PLACE_GENERAL_SWITCH_KEYS,
  PLACE_GENERAL_TEXT_KEYS,
  PlaceGeneralFormData,
} from './general-tab.constants'

const LINK_KEYS: readonly string[] = ['link', 'iosAppStoreLink', 'googlePlayLink']

// Note: same normalisation as the clients profile - Canadian postal codes become "A1A 1A1"
const normalizePostalCode = (value: string) => {
  if (!ZIP_CODE_CA_REGEX.test(value)) return value

  const compact = value.replace(/\s/g, '').toUpperCase()
  return `${compact.slice(0, 3)} ${compact.slice(3)}`
}

export const pickGeneralFlags = (values: PlaceGeneralFormData): PlaceFlags =>
  Object.fromEntries(PLACE_GENERAL_FLAG_KEYS.map((key) => [key, values[key]]))

interface GetPlaceGeneralPayloadParams {
  isAdmin: boolean
  server: Building
  values: PlaceGeneralFormData
}

export const getPlaceGeneralPayload = ({
  isAdmin,
  server,
  values,
}: GetPlaceGeneralPayloadParams) => {
  const defaults = getPlaceGeneralDefaults(server)
  const scalars: Record<string, unknown> = {
    code: values.code.trim(),
    tags: values.tags,
  }

  PLACE_GENERAL_TEXT_KEYS.forEach((key) => {
    if (key === 'hubspotDealId' && !isAdmin) return

    const next = (values[key] ?? '').trim()
    if (next === (defaults[key] ?? '').trim()) return

    scalars[key] = key === 'postalCode' ? normalizePostalCode(next) : next
    if (LINK_KEYS.includes(key)) scalars[key] = normalizeLink(next)
  })

  const phoneDigits = getPhoneDigits(values.phone)
  if (phoneDigits !== getPhoneDigits(defaults.phone)) {
    scalars.phone = toBuildingPhone(phoneDigits)
  }

  const residents = values.buildingResidence?.trim() ?? ''
  if (residents && residents !== defaults.buildingResidence) {
    scalars.buildingResidence = Number(residents)
  }

  // Note: a building without a stored timezone is shown as Central, so it is sent even when unchanged
  if (values.timezone !== defaults.timezone || !server.timezone) scalars.timezone = values.timezone
  if (values.logo?.[0]) scalars.logo = values.logo[0]
  if (values.image?.[0]) scalars.image = values.image[0]

  if (IS_CONVENIENCE_FEE_ENABLED) {
    const rate = values.residentConvenienceFeeRate ?? ''
    // Note: an empty rate is sent as '', the backend stores it as 0 (no fee)
    if (rate !== defaults.residentConvenienceFeeRate) {
      scalars.residentConvenienceFeeRate = rate === '' ? '' : Number(rate)
    }
    PLACE_GENERAL_SWITCH_KEYS.forEach((key) => {
      if (values[key] !== defaults[key]) scalars[key] = values[key]
    })
  }

  const nextFlags = pickGeneralFlags(values)
  const flags = buildFlagsPayload({ isAdmin, next: nextFlags, server })

  const payload: Record<string, unknown> = { ...scalars, ...flags }

  return { nextFlags, payload }
}
